/**
 * `pending-applitools-visual-ai` v1.2 — cross-browser visual matrix
 * orchestration contracts.
 *
 * One flow + one matrix definition → N parallel runs, one per cell.
 * After all cells finish, every non-anchor cell's screenshot is
 * compared against the anchor's via the existing pure-JS SSIM
 * helper (`packages/api/src/lib/visual-ai/ssim.ts`). Result is a
 * `VisualMatrixCellResult` array — the UI panel renders one column
 * per entry with a passed / failed / unknown badge.
 *
 * Vendor-agnostic by construction: the cells declare
 * `ExecutionCapability` tuples; the existing
 * `capability-resolver` (`packages/api/src/services/capability-resolver.ts`)
 * picks the right provider per cell. Local + Steel + BrowserStack +
 * Sauce Labs + LambdaTest are all already wired through.
 *
 * Design choices:
 *   • The anchor is selected by label, not by index, so that
 *     re-ordering the cell list in admin-config doesn't silently
 *     change which cell is the "ground truth".
 *   • `expandMatrix(config)` returns a flat array of capability
 *     cells the matrix-runner submits to the existing parallel-
 *     runs `StartParallelRunRequest`. Pure function — no fetch,
 *     no DB, easy to test.
 *   • `aggregateMatrixResults(...)` operates against an opaque
 *     `MatrixCellArtifact` shape (just `{ label, screenshot? }`)
 *     so the contracts module doesn't pull in the SSIM helper
 *     itself — the API service injects the comparator.
 *   • Anchor failure → every cell `unknown`. We never blame a
 *     non-anchor cell for a missing anchor screenshot.
 */

import { z } from 'zod';
import { ExecutionCapabilitySchema } from './run-capabilities';

/**
 * A single cell in a visual-matrix submission. Carries the
 * operator-facing `label` (rendered as the column header in the
 * grid UI) plus the validated `ExecutionCapability` tuple the
 * capability resolver routes on.
 */
export const VisualMatrixCellSchema = z
  .object({
    label: z.string().min(1).max(64),
    capability: ExecutionCapabilitySchema,
  })
  .strict();
export type VisualMatrixCell = z.infer<typeof VisualMatrixCellSchema>;

/**
 * Submit-time request body for `POST /api/visual-matrix/run`
 * (route lands in v1.2.1; the schema is forward-compatible).
 *
 * `anchorCellLabel` resolves to whichever cell in `cells` matches
 * the label. When unset or no match, the first cell wins (the
 * `pickAnchor()` helper enforces this).
 *
 * `cells` is capped at 20 (mirroring `parallel-runs` global cap)
 * and floored at 2 (one cell isn't a matrix — that's just a regular
 * baseline run). The per-org `visualAi.matrix.maxCells` may impose
 * a tighter cap at run-submit time.
 */
export const StartVisualMatrixRunRequestSchema = z.object({
  flowId: z.string().min(1),
  flowName: z.string().min(1).optional(),
  projectId: z.string().uuid().optional(),
  environment: z.string().min(1).optional(),
  cells: z.array(VisualMatrixCellSchema).min(2).max(20),
  /**
   * Optional anchor pin. When set, this exact label is the anchor
   * (errors with `VISUAL_MATRIX_ANCHOR_NOT_FOUND` if no cell
   * matches). When unset, `cells[0]` is the anchor.
   */
  anchorCellLabel: z.string().min(1).max(64).optional(),
  /**
   * Per-run SSIM threshold override. Falls back to the admin
   * config `visualAi.ssimThreshold` (default 0.97) when unset.
   */
  ssimThreshold: z.number().min(0).max(1).optional(),
});
export type StartVisualMatrixRunRequest = z.infer<typeof StartVisualMatrixRunRequestSchema>;

/**
 * Verdict for one cell in the aggregated result. `unknown` is
 * reserved for the anchor-failure case (we don't know how that
 * cell would have compared because we can't generate a diff).
 */
export const VisualMatrixCellVerdictSchema = z.enum(['passed', 'failed', 'anchor', 'unknown']);
export type VisualMatrixCellVerdict = z.infer<typeof VisualMatrixCellVerdictSchema>;

export const VisualMatrixCellResultSchema = z
  .object({
    label: z.string(),
    capability: ExecutionCapabilitySchema,
    verdict: VisualMatrixCellVerdictSchema,
    /**
     * SSIM score against the anchor cell. `null` for the anchor
     * row itself, the unknown rows (anchor failed), and any cell
     * that failed to capture its own screenshot.
     */
    ssimScore: z.number().min(0).max(1).nullable(),
    /**
     * Short human-readable note explaining the verdict — used in
     * tooltips on the grid. Examples: "SSIM 0.945 < 0.970",
     * "Anchor screenshot unavailable", "Screenshot capture
     * failed for this cell".
     */
    notes: z.string().nullable(),
  })
  .strict();
export type VisualMatrixCellResult = z.infer<typeof VisualMatrixCellResultSchema>;

export const VisualMatrixRunResultSchema = z
  .object({
    parentRunId: z.string().uuid(),
    flowId: z.string(),
    anchorCellLabel: z.string(),
    ssimThreshold: z.number().min(0).max(1),
    cells: z.array(VisualMatrixCellResultSchema),
    summary: z.object({
      total: z.number().int().min(0),
      passed: z.number().int().min(0),
      failed: z.number().int().min(0),
      unknown: z.number().int().min(0),
      worstScore: z.number().min(0).max(1).nullable(),
    }),
  })
  .strict();
export type VisualMatrixRunResult = z.infer<typeof VisualMatrixRunResultSchema>;

/**
 * Opaque artifact shape `aggregateMatrixResults` consumes. Holds
 * the per-cell screenshot the runner captured plus a per-cell
 * "did this cell even produce a screenshot?" flag. Contracts
 * module doesn't care what shape the screenshot is — the SSIM
 * comparator the caller passes in does.
 */
export interface MatrixCellArtifact<T> {
  label: string;
  /** Screenshot payload. `null` when the cell's run failed to
   *  capture (network error, browser crashed, etc.). */
  screenshot: T | null;
  /** Optional capture-side error string the runner emits for
   *  the UI tooltip. */
  captureError?: string | null;
}

/**
 * SSIM comparator the caller injects. Must return a score in
 * `[0, 1]` (1 = identical). The API wrapper passes
 * `(a, b) => ssim(a, b).meanScore` from
 * `@rynora/api/lib/visual-ai/ssim` so this contracts module
 * stays runtime-clean (no node:fs / canvas / sharp imports).
 */
export type SsimComparator<T> = (anchor: T, candidate: T) => number;

/**
 * Pure helper — resolve the anchor cell given a label hint and
 * the cell list. Returns `null` when `anchorHint` is set but no
 * cell matches (the caller raises `VISUAL_MATRIX_ANCHOR_NOT_FOUND`).
 * Returns the first cell when `anchorHint` is unset.
 */
export function pickAnchor(
  cells: ReadonlyArray<{ label: string }>,
  anchorHint?: string | null,
): { index: number; label: string } | null {
  if (cells.length === 0) return null;
  if (anchorHint) {
    const idx = cells.findIndex((c) => c.label === anchorHint);
    if (idx === -1) return null;
    return { index: idx, label: cells[idx].label };
  }
  return { index: 0, label: cells[0].label };
}

/**
 * Pure helper — given a matrix request, return the flat list of
 * `(label, capability)` pairs the parallel-runs fan-out worker
 * submits as `StartParallelRunRequest.capabilities`. Drops nothing,
 * preserves order. Asserts the anchor exists (caller has already
 * called `pickAnchor` and guarded on `null`).
 */
export function expandMatrix(req: StartVisualMatrixRunRequest): ReadonlyArray<VisualMatrixCell> {
  return req.cells;
}

/**
 * Pure helper — given the matrix request, the anchor label, the
 * SSIM threshold, and an artifact per cell, produce one
 * `VisualMatrixCellResult` per cell + the summary.
 *
 * Verdict rules:
 *   • The anchor cell always gets `verdict: 'anchor'`, `ssimScore: null`.
 *   • If the anchor's artifact has no screenshot, every non-anchor
 *     cell gets `verdict: 'unknown'`, `ssimScore: null` (we don't
 *     blame the wrong browser).
 *   • Otherwise per non-anchor cell: if it has no screenshot,
 *     `verdict: 'unknown'`, `ssimScore: null`; if it has one,
 *     compute SSIM and verdict against the threshold.
 *
 * `summary.worstScore` is the minimum SSIM across non-anchor cells
 * that produced a score; `null` when no cell produced one.
 */
export function aggregateMatrixResults<T>(
  req: StartVisualMatrixRunRequest,
  anchorLabel: string,
  ssimThreshold: number,
  artifacts: ReadonlyArray<MatrixCellArtifact<T>>,
  parentRunId: string,
  compare: SsimComparator<T>,
): VisualMatrixRunResult {
  const artifactByLabel = new Map(artifacts.map((a) => [a.label, a]));
  const anchorArtifact = artifactByLabel.get(anchorLabel) ?? null;
  const anchorMissing = !anchorArtifact || anchorArtifact.screenshot == null;

  const cellResults: VisualMatrixCellResult[] = req.cells.map((cell) => {
    if (cell.label === anchorLabel) {
      return {
        label: cell.label,
        capability: cell.capability,
        verdict: 'anchor',
        ssimScore: null,
        notes: anchorMissing
          ? (anchorArtifact?.captureError ?? 'Anchor screenshot unavailable')
          : 'Anchor cell — comparison reference',
      };
    }
    if (anchorMissing) {
      return {
        label: cell.label,
        capability: cell.capability,
        verdict: 'unknown',
        ssimScore: null,
        notes: 'Anchor screenshot unavailable — diff cannot be computed.',
      };
    }
    const art = artifactByLabel.get(cell.label);
    if (!art || art.screenshot == null) {
      return {
        label: cell.label,
        capability: cell.capability,
        verdict: 'unknown',
        ssimScore: null,
        notes: art?.captureError ?? 'Screenshot capture failed for this cell.',
      };
    }
    const score = compare(anchorArtifact!.screenshot!, art.screenshot);
    const clamped = Math.max(0, Math.min(1, score));
    const passed = clamped >= ssimThreshold;
    return {
      label: cell.label,
      capability: cell.capability,
      verdict: passed ? 'passed' : 'failed',
      ssimScore: clamped,
      notes: passed
        ? `SSIM ${clamped.toFixed(3)} ≥ ${ssimThreshold.toFixed(3)}`
        : `SSIM ${clamped.toFixed(3)} < ${ssimThreshold.toFixed(3)}`,
    };
  });

  let worst: number | null = null;
  for (const r of cellResults) {
    if (r.ssimScore == null) continue;
    if (worst == null || r.ssimScore < worst) worst = r.ssimScore;
  }

  return {
    parentRunId,
    flowId: req.flowId,
    anchorCellLabel: anchorLabel,
    ssimThreshold,
    cells: cellResults,
    summary: {
      total: cellResults.length,
      passed: cellResults.filter((c) => c.verdict === 'passed').length,
      failed: cellResults.filter((c) => c.verdict === 'failed').length,
      unknown: cellResults.filter((c) => c.verdict === 'unknown').length,
      worstScore: worst,
    },
  };
}
